import { defineStore } from "pinia";
import { ref } from "vue";
import type { RouteRecordName } from "vue-router";

/**
 * Contexte sanitaire — SFD-07 R15. L'agent de saisie d'un traitement est mémorisé tant que
 * l'opérateur reste dans ce contexte, et perdu dès qu'il en sort.
 *
 * Reconnu sur le NOM de la route (voir router/index.ts), pas sur le chemin : un chemin comparé
 * tel quel manque une casse ou un paramètre différents.
 *
 * `Animal` (/ListeAnimaux) n'y est pas : c'est « la liste des animaux du centre » de l'ex. 2, où l'on
 * arrive en mode BASE. Le `watch` du mode dans Animal.vue couvre la sortie sans navigation.
 */
const CONTEXT_ROUTE_NAMES = new Set<string>([
    /** La liste ATE (/ListeAnimauxSanitaire). */
    "ListeAnimauxSanitaire",
    /** La pharmacie (/ListePharmacie). */
    "ListePharmacie",
    /** La gestion des évènements, où s'édite la feuille de soins (/GestionCatalogueSanitaire). */
    "AdminCatalogueSanitaire",
]);

export const useSanitaryContext = defineStore("sanitaryContext", () => {
    /**
     * Agent de saisie mémorisé ; 0 = aucun. Volontairement NON persisté : R15 veut qu'il soit
     * perdu, et un localStorage le ferait survivre à un rechargement — donc au changement de
     * poste ou d'opérateur dans la journée.
     */
    const entryAgentId = ref(0);

    const rememberEntryAgent = (agentId: number) => {
        entryAgentId.value = agentId;
    };

    const exit = () => {
        entryAgentId.value = 0;
    };

    const isInContext = (routeName: RouteRecordName | null | undefined) =>
        typeof routeName === "string" && CONTEXT_ROUTE_NAMES.has(routeName);

    const exitIfOutsideContext = (routeName: RouteRecordName | null | undefined) => {
        if (!isInContext(routeName)) exit();
    };

    return { entryAgentId, rememberEntryAgent, exit, isInContext, exitIfOutsideContext };
});
